// Root-level GA4 loader. Renders null. Public pages only — never admin,
// kiosk/check-in, TV, focus, or OAuth callback.

import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { getPublicSiteConfig } from '../data/api';
import { useAuth } from '../data/AuthContext';
import {
  ensureGtag,
  injectGtagScript,
  isGa4PublicPath,
  isValidGaMeasurementId,
} from '../data/ga4';

const INTERACTION_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'] as const;
const DEFER_MS = 10_000;

export function Ga4Loader() {
  const location = useLocation();
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const [measurementId, setMeasurementId] = useState('');

  useEffect(() => {
    let cancelled = false;
    getPublicSiteConfig()
      .then((cfg) => {
        if (cancelled) return;
        const id = (cfg.ga_measurement_id || '').trim();
        setMeasurementId(isValidGaMeasurementId(id) ? id.toUpperCase() : '');
      })
      .catch(() => {
        if (!cancelled) setMeasurementId('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const path = location.pathname;
  const search = location.search;
  const active =
    !!measurementId && !authLoading && isGa4PublicPath(path, isLoggedIn);

  useEffect(() => {
    if (!active) return;

    ensureGtag(measurementId);

    let injected = false;
    const inject = () => {
      if (injected) return;
      injected = true;
      injectGtagScript(measurementId);
    };

    INTERACTION_EVENTS.forEach((evt) => {
      window.addEventListener(evt, inject, { once: true, passive: true });
    });
    const timer = window.setTimeout(inject, DEFER_MS);

    return () => {
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, inject));
      window.clearTimeout(timer);
    };
  }, [active, measurementId]);

  useEffect(() => {
    if (!active) return;
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: `${path}${search}`,
      page_title: document.title,
    });
  }, [active, path, search]);

  return null;
}

/** Pathless router shell: mount the loader on every route, public and admin. */
export function RootShell() {
  return (
    <>
      <Ga4Loader />
      <Outlet />
    </>
  );
}
